import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);
const CartContext = createContext(null);
const WishlistContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useCart = () => useContext(CartContext);
export const useWishlist = () => useContext(WishlistContext);

const EMPTY_CART = { items: [], count: 0, subtotal: 0, shipping: 0, total: 0 };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = sessionStorage.getItem("vearn_admin_member_view");
    if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(async () => {
      if (token) {
        sessionStorage.removeItem("vearn_admin_member_view");
        delete api.defaults.headers.common.Authorization;
        try { const r = await api.get("/auth/me"); setUser(r.data); return; } catch { /* expired admin session */ }
      }
      setUser(null);
    });
  }, []);

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    setUser(r.data);
    return r.data;
  };
  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data);
    return r.data;
  };
  const startMemberView = async (memberId) => {
    const { data } = await api.post(`/admin/users/${memberId}/view-session`);
    sessionStorage.setItem("vearn_admin_member_view", data.access_token);
    api.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
    const member = await api.get("/auth/me");
    setUser(member.data);
    return member.data;
  };
  const stopMemberView = async () => {
    sessionStorage.removeItem("vearn_admin_member_view");
    delete api.defaults.headers.common.Authorization;
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };
  const logout = async () => {
    if (sessionStorage.getItem("vearn_admin_member_view")) {
      await stopMemberView();
      return;
    }
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, startMemberView, stopMemberView }}>
      {user?.member_view && <div className="fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-4 bg-amber-300 px-4 py-2 text-xs font-semibold text-slate-950 shadow-lg">
        <span>READ-ONLY MEMBER VIEW - {user.name}</span>
        <button type="button" onClick={async () => { await stopMemberView(); window.location.assign("/admin"); }} className="rounded-full bg-slate-950 px-3 py-1 text-white">Return to admin</button>
      </div>}
      {children}
    </AuthContext.Provider>
  );
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(EMPTY_CART);
  const [wishlistIds, setWishlistIds] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) {
      setCart(EMPTY_CART);
      setWishlistIds([]);
      return;
    }
    try {
      const r = await api.get("/cart");
      setCart(r.data);
    } catch {
      setCart(EMPTY_CART);
    }
    try {
      const r = await api.get("/wishlist");
      setWishlistIds(r.data.product_ids || []);
    } catch {
      setWishlistIds([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = async (product_id, qty = 1, variant_id = null) => {
    const r = await api.post("/cart/items", { product_id, qty, variant_id });
    setCart(r.data);
  };
  const update = async (product_id, qty, variant_id = null) => {
    const r = await api.patch(`/cart/items/${product_id}`, { product_id, qty, variant_id });
    setCart(r.data);
  };
  const remove = async (product_id, variant_id = null) => {
    const r = await api.delete(`/cart/items/${product_id}`, { params: { variant_id } });
    setCart(r.data);
  };
  const toggleWishlist = async (product_id) => {
    const r = await api.post(`/wishlist/${product_id}`);
    setWishlistIds(r.data.product_ids);
    return r.data.added;
  };

  return (
    <CartContext.Provider value={{ cart, refresh, add, update, remove }}>
      <WishlistContext.Provider value={{ wishlistIds, toggleWishlist }}>
        {children}
      </WishlistContext.Provider>
    </CartContext.Provider>
  );
}
